<template>
  <section class="delivery-status" aria-live="polite">
    <h3>{{ title }}</h3>
    <p v-if="loading">Checking email status…</p>
    <template v-else-if="status">
      <strong>{{ label }}</strong>
      <p>{{ detail }}</p>
      <p v-if="status.setup_issue" role="alert">{{ status.setup_issue }}</p>
      <p v-if="status.updated_at">Last update: {{ formattedTime }}</p>
      <p>Email handoff does not confirm inbox delivery. Seller sale alerts are tracked separately.</p>
    </template>
    <p v-if="error" role="alert">{{ error }}</p>
    <div class="delivery-status__actions">
      <button type="button" :disabled="loading || sending" @click="load">Refresh status</button>
      <button v-if="status && status.can_resend && status.applicable !== false" type="button" :disabled="sending || loading" @click="confirming = true">Resend email</button>
    </div>
    <div v-if="confirming" class="delivery-status__confirm">
      <p>{{ confirmation }}</p>
      <button type="button" :disabled="sending" @click="send">{{ sending ? 'Requesting…' : 'Send email' }}</button>
      <button type="button" :disabled="sending" @click="confirming = false">Cancel</button>
    </div>
  </section>
</template>
<script>
import axios from 'axios';
const LABELS = { not_recorded: 'No recorded attempt', queued: 'Queued', sending: 'Sending', submitted: 'Submitted to email handoff', handed_off: 'Handed to mail service', blocked: 'Delivery blocked', failed: 'Could not prepare email', uncertain: 'Delivery outcome needs review' };
const REASONS = { missing_sender: 'Choose a verified sender in Funnel Settings → Transaction emails.', no_files: 'No downloadable files are configured.', files_unavailable: 'A configured download is unavailable. Review product Fulfillment.', suppressed: 'This address is on the suppression list.', hard_bounce: 'This address previously returned a permanent delivery failure.', no_mx: 'The address domain cannot receive mail.', invalid_format: 'Check the recipient email address.', account_email_invalid: 'The recipient address needs to be corrected.', order_ineligible: 'This order is not eligible for file delivery.', queue_uncertain: 'The queue request could not be confirmed. Do not repeat it until its outcome is checked.', delivery_uncertain: 'The mail service may have accepted this email. Review the outcome before sending another copy.' };
export default {
  props: { statusUrl: { type: String, required: true }, resendUrl: { type: String, required: true }, payload: { type: Object, default: () => ({}) }, title: { type: String, default: 'Buyer download email' }, confirmation: { type: String, default: 'Send the current download links to the buyer on this order? Membership access and payments will not change.' } },
  data() { return { status: null, loading: false, sending: false, confirming: false, error: '', requestId: null, generation: 0 }; },
  computed: {
    label() { return this.status.applicable === false ? 'No file email configured' : LABELS[this.status.state] || 'Status unavailable'; },
    detail() { return REASONS[this.status.reason] || (this.status.state === 'not_recorded' ? 'Older emails may have no status record. This does not mean they were never sent.' : 'Refresh to check the latest recorded outcome.'); },
    formattedTime() { return new Date(this.status.updated_at).toLocaleString(); },
  },
  watch: { statusUrl: { immediate: true, handler() { this.generation++; this.sending = false; this.status = null; this.confirming = false; this.requestId = null; this.load(); } } },
  methods: {
    async load() {
      const generation = this.generation;
      this.loading = true; this.error = '';
      try { const response = await axios.get(this.statusUrl); if (generation === this.generation) this.status = response.data; }
      catch (error) { if (generation === this.generation) { this.status = null; this.error = 'Could not load email status. Refresh before requesting another email.'; } }
      finally { if (generation === this.generation) this.loading = false; }
    },
    async send() {
      if (this.sending) return;
      const generation = this.generation;
      this.sending = true; this.error = '';
      // Keep the same id after an uncertain HTTP outcome, so a retry cannot enqueue a second copy.
      try {
      if (!this.requestId) this.requestId = Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('');
        await axios.post(this.resendUrl, { ...this.payload, request_id: this.requestId });
        if (generation !== this.generation) return;
        this.requestId = null; this.confirming = false; await this.load();
      } catch (error) {
        if (generation === this.generation) this.error = (error.response && error.response.data && error.response.data.message) || 'The request outcome could not be confirmed. Refresh email status before trying again.';
      } finally { if (generation === this.generation) this.sending = false; }
    },
  },
};
</script>
<style scoped>
.delivery-status { padding: 18px; margin: 16px 0; border: 1px solid #dce2ec; border-radius: 8px; color: #27334b; background: #f8faff; }
.delivery-status h3 { margin: 0 0 10px; font-size: 16px; }
.delivery-status p { margin: 8px 0; font-size: 13px; line-height: 1.5; overflow-wrap: anywhere; }
.delivery-status__actions { display: flex; flex-wrap: wrap; gap: 10px; }
.delivery-status button { min-height: 40px; border: 1px solid #96a7c6; padding: 8px 12px; border-radius: 6px; background: white; color: #234b8d; cursor: pointer; }
.delivery-status button:disabled { opacity: .5; cursor: wait; }
.delivery-status button:focus-visible { outline: 3px solid #7199ef; outline-offset: 2px; }
.delivery-status__confirm { margin-top: 12px; padding: 12px; background: #fff4df; border-radius: 6px; }
</style>
